// ============================================================================
// DENS CAKRA — Modul Pemantauan & Pelacakan Pergerakan Personel
// Sumber data operasional personel (deterministik, dummy realistis).
//
// Modul ini TIDAK mengubah data/store.ts. Ia memperkaya tiap personel
// (dari store + roster nasional turunan BINDA_LIST) dengan posisi terkini,
// riwayat pergerakan, log aktivitas, check-in, mode patroli, relasi laporan,
// dan KPI rinci. Semua data dihasilkan secara deterministik dari ID personel
// sehingga stabil di setiap render.
// ============================================================================

import { BINDA_LIST } from './binda'

// ---------------------------------------------------------------------------
// Tipe
// ---------------------------------------------------------------------------

export type StatusOperatif =
  | 'Aktif'
  | 'Siaga'
  | 'Bertugas'
  | 'Monitoring'
  | 'Patroli'
  | 'Dalam Perjalanan'
  | 'Offline'
  | 'Tidak Aktif'
  | 'Panic Alert'

export type StatusPatroli =
  | 'Patrolling'
  | 'Monitoring'
  | 'Observation'
  | 'Surveillance'
  | 'Standby'

export interface GeoPoint {
  lat: number
  lng: number
}

export interface CurrentPosition extends GeoPoint {
  address: string
  provinsi: string
  kabupaten: string
  kecamatan: string
  timestamp: string
  accuracy: number // meter
}

export interface MovementPoint extends GeoPoint {
  time: string
  label: string
  note: string
}

export interface ActivityLogEntry {
  id: string
  type:
    | 'Login'
    | 'Logout'
    | 'Buat Laporan'
    | 'Kirim Laporan'
    | 'Draf Tersimpan'
    | 'Panic Alert'
    | 'Sinkronisasi'
    | 'Pembaruan GPS'
  date: string
  time: string
  location: string
  device: string
  result: 'Berhasil' | 'Gagal' | 'Peringatan'
}

export interface CheckInEntry {
  id: string
  kind: 'Check In' | 'Check Out'
  timestamp: string
  lat: number
  lng: number
  location: string
  hasPhoto: boolean
}

export interface PatrolInfo {
  status: StatusPatroli
  currentRoute: MovementPoint[]
  completedRoute: MovementPoint[]
}

export interface ReportRelation {
  id: string
  title: string
  state: 'Dibuat' | 'Diverifikasi' | 'Diterima' | 'Ditolak'
  time: string
}

export interface PersonnelKpi {
  score: number
  trend: number // delta vs periode lalu
  rankingBinda: number
  rankingBindaTotal: number
  rankingNasional: number
  rankingNasionalTotal: number
  productivity: number
  quality: number
  timeliness: number
  acceptanceRate: number
  history: { label: string; value: number }[]
}

export interface PersonnelOps {
  id: string
  name: string
  photo: string
  binda: string
  bindaKode: string
  unit: string
  jabatan: string
  role: string
  wilayahTugas: string
  provinsi: string
  status: StatusOperatif
  kpiScore: number
  lastActivity: string
  lastSync: string
  device: string
  deviceStatus: 'Online' | 'Offline' | 'Sinkronisasi' | 'Baterai Lemah'
  clearance: string
  // Operasional
  currentPosition: CurrentPosition
  lastKnownPosition: CurrentPosition
  lastSeen: string
  movementHistory: MovementPoint[]
  activityLog: ActivityLogEntry[]
  checkIns: CheckInEntry[]
  patrol: PatrolInfo
  reports: ReportRelation[]
  kpi: PersonnelKpi
  // ringkasan relasi laporan
  reportsCreated: number
  reportsVerified: number
  reportsAccepted: number
  reportsRejected: number
}

// ---------------------------------------------------------------------------
// Util deterministik (PRNG berbasis hash dari ID)
// ---------------------------------------------------------------------------

function hash(str: string): number {
  let h = 2166136261
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

function makeRng(seed: string) {
  let s = hash(seed) || 1
  return () => {
    s ^= s << 13
    s ^= s >>> 17
    s ^= s << 5
    s >>>= 0
    return s / 4294967296
  }
}

function pick<T>(rng: () => number, arr: T[]): T {
  return arr[Math.floor(rng() * arr.length)]
}

function pad(n: number): string {
  return String(n).padStart(2, '0')
}

// ---------------------------------------------------------------------------
// Kamus dummy realistis
// ---------------------------------------------------------------------------

const STATUS_POOL: StatusOperatif[] = [
  'Aktif',
  'Siaga',
  'Bertugas',
  'Monitoring',
  'Patroli',
  'Dalam Perjalanan',
  'Offline',
  'Tidak Aktif',
  'Panic Alert',
]

// Bobot agar mayoritas online/aktif, sebagian kecil offline / alert
const STATUS_WEIGHTED: StatusOperatif[] = [
  'Aktif',
  'Aktif',
  'Aktif',
  'Bertugas',
  'Bertugas',
  'Monitoring',
  'Patroli',
  'Siaga',
  'Dalam Perjalanan',
  'Offline',
  'Tidak Aktif',
  'Panic Alert',
]

export const PATROL_STATUSES: StatusPatroli[] = [
  'Patrolling',
  'Monitoring',
  'Observation',
  'Surveillance',
  'Standby',
]

// Pemetaan warna marker peta sesuai brief:
// Green = Active, Yellow = Standby, Blue = On Duty, Red = Panic Alert, Gray = Offline
export function statusMarkerColor(status: StatusOperatif): string {
  switch (status) {
    case 'Aktif':
      return 'var(--status-active)' // hijau
    case 'Siaga':
      return 'var(--status-standby)' // kuning/oranye
    case 'Bertugas':
    case 'Monitoring':
    case 'Patroli':
    case 'Dalam Perjalanan':
      return 'var(--status-info)' // biru
    case 'Panic Alert':
      return 'var(--destructive)' // merah
    case 'Offline':
    case 'Tidak Aktif':
    default:
      return 'var(--muted-foreground)' // abu-abu
  }
}

export function isOnline(status: StatusOperatif): boolean {
  return status !== 'Offline' && status !== 'Tidak Aktif'
}

export const STATUS_OPERATIF_LIST = STATUS_POOL

const UNITS = [
  'Operasi Lapangan',
  'Pengamanan Objek Vital',
  'Kontra Intelijen',
  'Analisis Siber',
  'Pengumpulan HUMINT',
  'Pengawasan Wilayah',
  'Reaksi Cepat',
]

const JABATAN = [
  'Operator Lapangan',
  'Senior Operator',
  'Koordinator Tim',
  'Analis Intelijen',
  'Pengawas Wilayah',
  'Perwira Penghubung',
]

const ROLES = [
  'Personel Lapangan',
  'Analis',
  'Koordinator Wilayah',
  'Operator Command Center',
]

const KECAMATAN_POOL = [
  'Menteng',
  'Cibeunying',
  'Gubeng',
  'Denpasar Utara',
  'Sukajadi',
  'Tanah Abang',
  'Gambir',
  'Kebayoran Baru',
  'Coblong',
  'Wonokromo',
]

const KABUPATEN_POOL = [
  'Kota Pusat',
  'Kabupaten Utara',
  'Kota Timur',
  'Kabupaten Selatan',
  'Kota Barat',
]

const DEVICES = ['DENS-FLD-', 'DENS-MOB-', 'DENS-TAC-']

const MOVEMENT_LABELS = [
  {
    label: 'Kantor BINDA',
    note: 'Mulai shift, briefing pagi & ambil perangkat.',
  },
  {
    label: 'Wilayah Operasi A',
    note: 'Tiba di area operasi prioritas sektor utara.',
  },
  {
    label: 'Objek Monitoring',
    note: 'Pengamatan objek vital, dokumentasi situasi.',
  },
  {
    label: 'Wilayah Operasi B',
    note: 'Berpindah ke sektor selatan untuk verifikasi.',
  },
  {
    label: 'Lokasi Pelaporan',
    note: 'Menyusun & mengirim laporan kejadian dari lapangan.',
  },
  {
    label: 'Kembali ke Posko',
    note: 'Kembali ke posko, sinkronisasi data & debrief.',
  },
]

const MOVEMENT_TIMES = ['08:15', '09:02', '10:15', '11:37', '13:25', '15:01']

const STREETS = [
  'Jl. Merdeka',
  'Jl. Sudirman',
  'Jl. Diponegoro',
  'Jl. Gatot Subroto',
  'Jl. Ahmad Yani',
  'Jl. Pahlawan',
]

// ---------------------------------------------------------------------------
// Roster: store personnel + roster nasional turunan BINDA_LIST
// ---------------------------------------------------------------------------

interface RosterSeed {
  id: string
  name: string
  role?: string
  bindaKode: string
  clearance?: string
}

const FIRST_NAMES = [
  'Andika',
  'Bayu',
  'Citra',
  'Dewi',
  'Eka',
  'Fajar',
  'Gilang',
  'Hesti',
  'Indra',
  'Joko',
  'Kirana',
  'Laksmi',
  'Maulana',
  'Nadia',
  'Oscar',
  'Putri',
  'Reza',
  'Sari',
  'Tegar',
  'Wahyu',
]
const LAST_NAMES = [
  'Pratama',
  'Wijaya',
  'Santoso',
  'Hidayat',
  'Nugroho',
  'Saputra',
  'Permana',
  'Halim',
  'Kusuma',
  'Wardhana',
]

// Bangun roster nasional: 1–2 personel per BINDA + personel inti store.
function buildNationalRoster(): RosterSeed[] {
  const roster: RosterSeed[] = []
  BINDA_LIST.forEach((b, idx) => {
    const rng = makeRng(b.kode + '-roster')
    const count = b.personelAktif > 400 ? 3 : b.personelAktif > 200 ? 2 : 1
    for (let i = 0; i < count; i++) {
      const fn = pick(rng, FIRST_NAMES)
      const ln = pick(rng, LAST_NAMES)
      roster.push({
        id: `PRS-${b.kode.replace('BINDA-', '')}-${pad(i + 1)}`,
        name: `${fn} ${ln}`,
        role: pick(rng, ROLES),
        bindaKode: b.kode,
        clearance: `Tingkat ${2 + Math.floor(rng() * 3)}`,
      })
    }
  })
  return roster
}

// Personel inti yang berasal dari store (agar konsisten dengan /personnel store)
const CORE_SEEDS: RosterSeed[] = [
  {
    id: 'OP-772',
    name: 'Agen Alpha',
    role: 'Personel Lapangan',
    bindaKode: 'BINDA-JK',
    clearance: 'Tingkat 3',
  },
  {
    id: 'OP-891',
    name: 'Agen Bravo',
    role: 'Personel Lapangan',
    bindaKode: 'BINDA-JB',
    clearance: 'Tingkat 3',
  },
  {
    id: 'AN-102',
    name: 'Analis Charlie',
    role: 'Analis',
    bindaKode: 'BINDA-JK',
    clearance: 'Tingkat 4',
  },
  {
    id: 'OP-445',
    name: 'Agen Delta',
    role: 'Personel Lapangan',
    bindaKode: 'BINDA-PA',
    clearance: 'Tingkat 3',
  },
  {
    id: 'KB-001',
    name: 'Direktur Echo',
    role: 'Kepala BINDA',
    bindaKode: 'BINDA-BA',
    clearance: 'Tingkat 5',
  },
]

let _rosterCache: PersonnelOps[] | null = null

function bindaByKode(kode: string) {
  return BINDA_LIST.find((b) => b.kode === kode) || BINDA_LIST[0]
}

// ---------------------------------------------------------------------------
// Generator detail operasional per personel
// ---------------------------------------------------------------------------

function buildOps(seed: RosterSeed): PersonnelOps {
  const rng = makeRng(seed.id)
  const b = bindaByKode(seed.bindaKode)
  const status = pick(rng, STATUS_WEIGHTED)
  const role = seed.role || pick(rng, ROLES)
  const unit = pick(rng, UNITS)
  const jabatan = pick(rng, JABATAN)
  const kpiScore = 72 + Math.floor(rng() * 27)
  const online = isOnline(status)
  const photoIdx = (hash(seed.id) % 70) + 1
  const kecamatan = pick(rng, KECAMATAN_POOL)
  const kabupaten = pick(rng, KABUPATEN_POOL)
  const deviceStatus: PersonnelOps['deviceStatus'] = !online
    ? 'Offline'
    : status === 'Dalam Perjalanan'
      ? 'Sinkronisasi'
      : rng() > 0.85
        ? 'Baterai Lemah'
        : 'Online'

  // Posisi terkini: sekitar koordinat BINDA dengan jitter realistis
  const jitter = () => (rng() - 0.5) * 0.18
  const baseLat = b.lat + jitter()
  const baseLng = b.lng + jitter()
  const lastSyncMin = online
    ? Math.floor(rng() * 9)
    : 40 + Math.floor(rng() * 600)

  const currentPosition: CurrentPosition = {
    lat: +baseLat.toFixed(5),
    lng: +baseLng.toFixed(5),
    address: `${pick(rng, STREETS)} No. ${10 + Math.floor(rng() * 80)}, ${kecamatan}`,
    provinsi: b.provinsi,
    kabupaten: `${kabupaten} ${b.provinsi.split(' ')[0]}`,
    kecamatan,
    timestamp: relTime(lastSyncMin),
    accuracy: 5 + Math.floor(rng() * 22),
  }

  // Riwayat pergerakan: 6 titik bergerak melingkar dari posko
  const movementHistory: MovementPoint[] = MOVEMENT_LABELS.map((m, i) => {
    const t = i / (MOVEMENT_LABELS.length - 1)
    return {
      lat: +(
        b.lat +
        (rng() - 0.5) * 0.08 +
        Math.sin(t * Math.PI) * 0.06
      ).toFixed(5),
      lng: +(
        b.lng +
        (rng() - 0.5) * 0.08 +
        Math.cos(t * Math.PI) * 0.06
      ).toFixed(5),
      time: MOVEMENT_TIMES[i],
      label: m.label,
      note: m.note,
    }
  })

  // Log aktivitas
  const activityTypes: ActivityLogEntry['type'][] = [
    'Login',
    'Pembaruan GPS',
    'Buat Laporan',
    'Draf Tersimpan',
    'Kirim Laporan',
    'Sinkronisasi',
    'Pembaruan GPS',
    status === 'Panic Alert' ? 'Panic Alert' : 'Sinkronisasi',
    'Logout',
  ]
  const device = `${pick(rng, DEVICES)}${100 + Math.floor(rng() * 900)}`
  const activityLog: ActivityLogEntry[] = activityTypes.map((type, i) => ({
    id: `${seed.id}-ACT-${pad(i + 1)}`,
    type,
    date: '24 Jun 2026',
    time: `${pad(8 + i)}:${pad(Math.floor(rng() * 60))}`,
    location:
      i % 2 === 0 ? movementHistory[i % movementHistory.length].label : b.binda,
    device,
    result:
      type === 'Panic Alert'
        ? 'Peringatan'
        : rng() > 0.94
          ? 'Gagal'
          : 'Berhasil',
  }))

  // Check-in / check-out
  const checkIns: CheckInEntry[] = [
    {
      id: `${seed.id}-CHK-01`,
      kind: 'Check In',
      timestamp: '24 Jun 2026 08:15',
      lat: movementHistory[0].lat,
      lng: movementHistory[0].lng,
      location: 'Kantor BINDA',
      hasPhoto: true,
    },
    {
      id: `${seed.id}-CHK-02`,
      kind: 'Check In',
      timestamp: '24 Jun 2026 10:15',
      lat: movementHistory[2].lat,
      lng: movementHistory[2].lng,
      location: 'Objek Monitoring',
      hasPhoto: rng() > 0.5,
    },
    {
      id: `${seed.id}-CHK-03`,
      kind: online ? 'Check Out' : 'Check Out',
      timestamp: '24 Jun 2026 15:01',
      lat: movementHistory[5].lat,
      lng: movementHistory[5].lng,
      location: 'Kembali ke Posko',
      hasPhoto: false,
    },
  ]

  // Patroli: rute selesai (3 titik awal) + rute aktif (sisanya)
  const patrol: PatrolInfo = {
    status: pick(rng, PATROL_STATUSES),
    completedRoute: movementHistory.slice(0, 3),
    currentRoute: movementHistory.slice(2),
  }

  // Relasi laporan — gunakan ID laporan nyata + ID turunan
  const reportPool: ReportRelation[] = [
    {
      id: 'REP-2023-001',
      title: 'Aktivitas Mencurigakan di Pelabuhan',
      state: 'Diterima',
      time: '27 Okt 14:30',
    },
    {
      id: 'REP-2023-003',
      title: 'Ancaman Siber Terdeteksi',
      state: 'Diverifikasi',
      time: '27 Okt 12:00',
    },
    {
      id: 'REP-2023-005',
      title: 'Drone Tidak Dikenal',
      state: 'Ditolak',
      time: '27 Okt 09:30',
    },
    {
      id: `REP-2026-${pad(100 + (hash(seed.id) % 80))}`,
      title: 'Pengamatan Objek Vital',
      state: 'Diterima',
      time: '24 Jun 11:37',
    },
    {
      id: `REP-2026-${pad(180 + (hash(seed.id) % 60))}`,
      title: 'Laporan Pergerakan Massa',
      state: 'Dibuat',
      time: '24 Jun 13:25',
    },
    {
      id: `REP-2026-${pad(240 + (hash(seed.id) % 50))}`,
      title: 'Verifikasi Lokasi Pelaporan',
      state: 'Diverifikasi',
      time: '24 Jun 14:10',
    },
  ]
  const reportCount = 3 + Math.floor(rng() * 3)
  const reports = reportPool.slice(0, reportCount)
  const reportsCreated = reports.length + Math.floor(rng() * 18)
  const reportsAccepted =
    reports.filter((r) => r.state === 'Diterima').length +
    Math.floor(rng() * 10)
  const reportsVerified =
    reports.filter((r) => r.state === 'Diverifikasi').length +
    Math.floor(rng() * 12)
  const reportsRejected =
    reports.filter((r) => r.state === 'Ditolak').length + Math.floor(rng() * 3)

  // KPI rinci
  const kpi: PersonnelKpi = {
    score: kpiScore,
    trend: +(rng() * 8 - 3).toFixed(1),
    rankingBinda: 1 + Math.floor(rng() * 30),
    rankingBindaTotal: b.personelAktif,
    rankingNasional: 1 + Math.floor(rng() * 1200),
    rankingNasionalTotal: 6500,
    productivity: 70 + Math.floor(rng() * 29),
    quality: 70 + Math.floor(rng() * 29),
    timeliness: 65 + Math.floor(rng() * 34),
    acceptanceRate: 78 + Math.floor(rng() * 21),
    history: ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun'].map((label, i) => ({
      label,
      value: Math.max(
        60,
        Math.min(99, kpiScore - 8 + i * 2 + Math.floor(rng() * 6)),
      ),
    })),
  }

  const lastActivity = relTime(lastSyncMin)
  const lastSync = relTime(lastSyncMin)
  const lastSeen = online ? lastSync : relTime(lastSyncMin)

  return {
    id: seed.id,
    name: seed.name,
    photo: `https://i.pravatar.cc/240?img=${photoIdx}`,
    binda: b.binda,
    bindaKode: b.kode,
    unit,
    jabatan,
    role,
    wilayahTugas: b.provinsi,
    provinsi: b.provinsi,
    status,
    kpiScore,
    lastActivity,
    lastSync,
    device,
    deviceStatus,
    clearance: seed.clearance || `Tingkat ${2 + Math.floor(rng() * 3)}`,
    currentPosition,
    lastKnownPosition: currentPosition,
    lastSeen,
    movementHistory,
    activityLog,
    checkIns,
    patrol,
    reports,
    kpi,
    reportsCreated,
    reportsVerified,
    reportsAccepted,
    reportsRejected,
  }
}

function relTime(minutes: number): string {
  if (minutes < 1) return 'Baru saja'
  if (minutes < 60) return `${minutes} mnt lalu`
  const h = Math.floor(minutes / 60)
  if (h < 24) return `${h} jam lalu`
  const d = Math.floor(h / 24)
  return `${d} hari lalu`
}

// ---------------------------------------------------------------------------
// API publik
// ---------------------------------------------------------------------------

export function getRoster(): PersonnelOps[] {
  if (_rosterCache) return _rosterCache
  const seeds = [...CORE_SEEDS, ...buildNationalRoster()]
  // dedup by id
  const seen = new Set<string>()
  const unique = seeds.filter((s) => {
    if (seen.has(s.id)) return false
    seen.add(s.id)
    return true
  })
  _rosterCache = unique.map(buildOps)
  return _rosterCache
}

export function getPersonnelOps(id: string): PersonnelOps | undefined {
  return getRoster().find((p) => p.id === id)
}

// Filter option pools (untuk filter peta & daftar)
export function getProvinsiOptions(): string[] {
  return Array.from(new Set(BINDA_LIST.map((b) => b.provinsi))).sort()
}

export function getBindaOptions(): { kode: string; binda: string }[] {
  return BINDA_LIST.map((b) => ({ kode: b.kode, binda: b.binda }))
}

export function getUnitOptions(): string[] {
  return UNITS
}

export function getRoleOptions(): string[] {
  return ROLES
}

export function getKabupatenOptions(): string[] {
  return Array.from(
    new Set(getRoster().map((p) => p.currentPosition.kabupaten)),
  ).sort()
}

export function getKecamatanOptions(): string[] {
  return KECAMATAN_POOL
}

export function kpiGrade(score: number): 'A' | 'B' | 'C' | 'D' {
  if (score >= 90) return 'A'
  if (score >= 80) return 'B'
  if (score >= 70) return 'C'
  return 'D'
}

// Hitung jarak haversine (km) untuk total jarak pergerakan
export function haversineKm(a: GeoPoint, b: GeoPoint): number {
  const R = 6371
  const dLat = ((b.lat - a.lat) * Math.PI) / 180
  const dLng = ((b.lng - a.lng) * Math.PI) / 180
  const lat1 = (a.lat * Math.PI) / 180
  const lat2 = (b.lat * Math.PI) / 180
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
  return R * 2 * Math.asin(Math.sqrt(h))
}

export function totalDistanceKm(points: GeoPoint[]): number {
  let total = 0
  for (let i = 1; i < points.length; i++) {
    total += haversineKm(points[i - 1], points[i])
  }
  return +total.toFixed(1)
}

// Ringkasan eksekutif
export interface ExecutiveSummary {
  total: number
  aktif: number
  bertugas: number
  offline: number
  patroli: number
  alert: number
  byRegion: { provinsi: string; count: number; alert: number }[]
}

export function getExecutiveSummary(roster: PersonnelOps[]): ExecutiveSummary {
  const byRegionMap = new Map<string, { count: number; alert: number }>()
  roster.forEach((p) => {
    const cur = byRegionMap.get(p.provinsi) || { count: 0, alert: 0 }
    cur.count += 1
    if (p.status === 'Panic Alert') cur.alert += 1
    byRegionMap.set(p.provinsi, cur)
  })
  return {
    total: roster.length,
    aktif: roster.filter((p) => p.status === 'Aktif').length,
    bertugas: roster.filter((p) =>
      ['Bertugas', 'Monitoring', 'Dalam Perjalanan'].includes(p.status),
    ).length,
    offline: roster.filter((p) => !isOnline(p.status)).length,
    patroli: roster.filter((p) => p.status === 'Patroli').length,
    alert: roster.filter((p) => p.status === 'Panic Alert').length,
    byRegion: Array.from(byRegionMap.entries())
      .map(([provinsi, v]) => ({ provinsi, count: v.count, alert: v.alert }))
      .sort((a, b) => b.count - a.count),
  }
}
